// -----------------------------------------------------------
// FloraIQ - Acoes de cadastramento (missoes de campo reais)
// -----------------------------------------------------------
// As "acoes de cadastramento" do metodo correspondem as missoes de campo
// cadastradas em /campanhas (cta.campaigns). Dado real, sem fallback.

import { Link } from "react-router-dom";
import { ErrorState, LoadingSkeleton, NoRealData } from "@/components/shared";
import type { CampaignSummary } from "@/types/campaign";
import { CAMPAIGN_STATUS_COLORS, CAMPAIGN_STATUS_LABELS, CAMPAIGN_TYPE_LABELS } from "@/types/campaign";

interface Props {
  campaigns: CampaignSummary[];
  loading: boolean;
  error: string | null;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

export default function CampaignsSection({ campaigns, loading, error }: Props) {
  if (loading) return <LoadingSkeleton type="table" count={4} />;
  if (error) return <ErrorState title="Não foi possível carregar as missões" message={error} />;
  if (campaigns.length === 0) {
    return (
      <NoRealData
        icon="ri-folder-chart-line"
        title="Nenhuma ação de cadastramento"
        description="As ações de cadastramento são as missões de campo. Nenhuma missão foi cadastrada nesta organização."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-foreground-500">
          Missões de campo cadastradas. Para criar ou editar uma missão, use o módulo{" "}
          <Link to="/campanhas" className="text-primary-600 hover:underline">Campanhas</Link>.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200 text-left text-[10px] font-semibold text-foreground-500 uppercase">
              <th className="py-2 px-3">Missão</th>
              <th className="py-2 px-3">Projeto / área</th>
              <th className="py-2 px-3">Tipo</th>
              <th className="py-2 px-3">Situação</th>
              <th className="py-2 px-3">Período</th>
              <th className="py-2 px-3 text-right">Quadras</th>
              <th className="py-2 px-3 text-right">Exemplares</th>
              <th className="py-2 px-3">Progresso</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} className="border-b border-background-100 hover:bg-background-50/50">
                <td className="py-2.5 px-3">
                  <p className="text-xs font-medium text-foreground-800">{c.name}</p>
                  <p className="text-[10px] text-foreground-400 font-mono">{c.code}</p>
                </td>
                <td className="py-2.5 px-3 text-xs text-foreground-600">
                  <p>{c.projectName}</p>
                  <p className="text-[10px] text-foreground-400">{c.areaPilotoName ?? "—"}</p>
                </td>
                <td className="py-2.5 px-3 text-xs text-foreground-600">{CAMPAIGN_TYPE_LABELS[c.campaignType] ?? c.campaignType}</td>
                <td className="py-2.5 px-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${CAMPAIGN_STATUS_COLORS[c.status] ?? "bg-background-100 text-foreground-500"}`}>
                    {CAMPAIGN_STATUS_LABELS[c.status] ?? c.status}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-[10px] text-foreground-500 whitespace-nowrap">
                  {formatDate(c.startDate)} a {formatDate(c.endDate)}
                </td>
                <td className="py-2.5 px-3 text-xs text-foreground-700 text-right">
                  {c.quadraCount > 0 ? `${c.quadrasConcluidas}/${c.quadraCount}` : "—"}
                </td>
                <td className="py-2.5 px-3 text-xs text-foreground-700 text-right">
                  {c.registeredTreeCount.toLocaleString("pt-BR")}
                  {c.targetTreeCount != null && <span className="text-foreground-400"> / {c.targetTreeCount.toLocaleString("pt-BR")}</span>}
                </td>
                <td className="py-2.5 px-3 min-w-[110px]">
                  {c.targetTreeCount != null || c.quadraCount > 0 ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-background-100 rounded-full h-1.5">
                        <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(0, c.progressPercent))}%` }}></div>
                      </div>
                      <span className="text-[10px] text-foreground-500">{Math.round(c.progressPercent)}%</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-foreground-400">Sem meta</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
